// 계정 설정 — 본인 정보 + 비밀번호 변경
import { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useAuth } from '../lib/auth'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

// 비밀번호 강도 점수 — 12자+ AND 종 class 수 (대/소/숫자/특수)
function strengthScore(pw: string): { score: 0 | 1 | 2 | 3; classes: number; lenOk: boolean } {
  const classes =
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0)
  const lenOk = pw.length >= 12
  if (!lenOk || classes < 3) return { score: 1, classes, lenOk }
  if (pw.length >= 16 && classes >= 4) return { score: 3, classes, lenOk }
  if (pw.length >= 12 && classes >= 3) return { score: 2, classes, lenOk }
  return { score: 0, classes, lenOk }
}

export function AccountPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const sc = useMemo(() => strengthScore(next), [next])
  const mismatch = confirm.length > 0 && next !== confirm
  const same = current.length > 0 && next.length > 0 && current === next
  const canSubmit =
    !busy && current.length > 0 && sc.lenOk && sc.classes >= 3 && !mismatch && !same

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    try {
      await api.post('/account/change-password', {
        current_password: current,
        new_password: next,
      })
      toast.push('success', t('acct.pw.success'))
      setCurrent(''); setNext(''); setConfirm('')
    } catch (err: any) {
      toast.push('error', err?.message ?? t('toast.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  const strengthBar = (
    <div role="progressbar" aria-valuenow={sc.score} aria-valuemin={0} aria-valuemax={3}
      style={{ display: 'flex', gap: 4, height: 6, marginTop: 6 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          flex: 1,
          borderRadius: 999,
          background: i < sc.score
            ? sc.score === 3 ? 'var(--color-success)'
              : sc.score === 2 ? 'var(--color-accent)'
              : 'var(--color-warn)'
            : 'var(--color-line)',
          transition: 'background 0.2s',
        }} />
      ))}
    </div>
  )

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="user" /> {t('acct.title')}
      </h1>
      <p className="muted reveal">{t('acct.intro')}</p>

      {/* 내 정보 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="profile-h">
        <h2 id="profile-h" style={{ fontSize: 'var(--fs-lg)' }}>{t('acct.profile.title')}</h2>
        {user ? (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem', margin: 0 }}>
            <dt className="muted">{t('acct.profile.email')}</dt>
            <dd className="mono" style={{ margin: 0 }}>{user.email}</dd>
            <dt className="muted">{t('acct.profile.tenant')}</dt>
            <dd className="mono" style={{ margin: 0 }}>{user.tenantId}</dd>
            <dt className="muted">{t('acct.profile.roles')}</dt>
            <dd style={{ margin: 0 }}>
              {(user.roles ?? []).map((r) => (
                <span key={r} className="badge info" style={{ marginRight: '0.25rem' }}>{r}</span>
              ))}
            </dd>
          </dl>
        ) : (
          <p className="muted">{t('common.loading')}</p>
        )}
      </Card>

      {/* 비밀번호 변경 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="pw-h">
        <h2 id="pw-h" style={{ fontSize: 'var(--fs-lg)' }}>
          <FontAwesomeIcon icon="lock" /> {t('acct.pw.title')}
        </h2>
        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="cur-pw">{t('acct.pw.current')}</label>
            <input
              id="cur-pw"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-pw">{t('acct.pw.new')}</label>
            <input
              id="new-pw"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-describedby="pw-policy"
            />
            {next.length > 0 && (
              <>
                {strengthBar}
                <p className="muted" style={{ margin: '0.4rem 0 0', fontSize: 'var(--fs-xs)' }}>
                  {sc.score >= 3 ? t('acct.pw.strengthHigh')
                    : sc.score === 2 ? t('acct.pw.strengthMid')
                    : t('acct.pw.strengthLow')} · {next.length} chars · {sc.classes}/4 classes
                </p>
              </>
            )}
            <p id="pw-policy" className="muted" style={{ margin: '0.3rem 0 0', fontSize: 'var(--fs-xs)' }}>
              {t('acct.pw.policy')}
            </p>
            {same && (
              <p role="alert" style={{ margin: '0.3rem 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-danger)' }}>
                {t('acct.pw.same')}
              </p>
            )}
          </div>
          <div className="field">
            <label htmlFor="confirm-pw">{t('acct.pw.confirm')}</label>
            <input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatch}
            />
            {mismatch && (
              <p role="alert" style={{ margin: '0.3rem 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-danger)' }}>
                {t('acct.pw.mismatch')}
              </p>
            )}
          </div>
          <Button
            type="submit"
            variant="accent"
            disabled={!canSubmit}
            aria-busy={busy}
            magnetic={false}
          >
            <FontAwesomeIcon icon={busy ? 'circle-notch' : 'check'} spin={busy} />
            <span>{t('acct.pw.submit')}</span>
          </Button>
        </form>
      </Card>
    </main>
  )
}
